import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { api } from '../lib/api';
import type { Store } from '../types';

type Tab = 'stores' | 'users';

type AdminUser = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  roles: ('admin' | 'customer_service' | 'produce_manager')[];
  stores: { id: string; name: string }[];
  createdAt: string;
};

export function AdminPage() {
  const { me } = useAuth();
  const [tab, setTab] = useState<Tab>('stores');
  const [storeDialog, setStoreDialog] = useState<{ open: boolean; editing?: Store }>({ open: false });
  const [userDialog, setUserDialog] = useState<{ open: boolean; editing?: AdminUser }>({ open: false });

  if (!me) return null;
  if (!me.roles.includes('admin')) return <Navigate to="/" replace />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-3xl font-bold text-slate-900">Administración</h1>
      </div>

      <div className="grid grid-cols-2 gap-1.5 bg-slate-100 rounded-xl p-1.5">
        <button
          type="button"
          onClick={() => setTab('stores')}
          className={`py-3 rounded-lg text-base font-semibold transition ${tab === 'stores' ? 'bg-white shadow text-brand-700' : 'text-slate-600'}`}
        >
          🏬 Tiendas
        </button>
        <button
          type="button"
          onClick={() => setTab('users')}
          className={`py-3 rounded-lg text-base font-semibold transition ${tab === 'users' ? 'bg-white shadow text-brand-700' : 'text-slate-600'}`}
        >
          👥 Usuarios
        </button>
      </div>

      {tab === 'stores' && (
        <StoresTab
          onAdd={() => setStoreDialog({ open: true })}
          onEdit={(s) => setStoreDialog({ open: true, editing: s })}
        />
      )}
      {tab === 'users' && (
        <UsersTab
          currentUserId={me.id}
          onAdd={() => setUserDialog({ open: true })}
          onEdit={(u) => setUserDialog({ open: true, editing: u })}
        />
      )}

      {storeDialog.open && (
        <StoreDialog editing={storeDialog.editing} onClose={() => setStoreDialog({ open: false })} />
      )}
      {userDialog.open && (
        <UserDialog editing={userDialog.editing} onClose={() => setUserDialog({ open: false })} />
      )}
    </div>
  );
}

// ----- Stores tab -----

function StoresTab({ onAdd, onEdit }: { onAdd: () => void; onEdit: (s: Store) => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: () => api<Store[]>('/api/v1/stores'),
  });
  const [confirming, setConfirming] = useState<Store | null>(null);
  const [error, setError] = useState<string | null>(null);

  const delMut = useMutation({
    mutationFn: (id: string) => api(`/api/v1/stores/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'stores'] });
      qc.invalidateQueries({ queryKey: ['stores'] });
      setConfirming(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Error al eliminar'),
  });

  return (
    <>
      <div className="flex justify-end">
        <button
          onClick={onAdd}
          className="px-5 py-3 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white text-base font-bold rounded-full shadow-md"
        >
          + Nueva tienda
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {q.isLoading && <div className="p-8 text-center text-slate-400 text-base">Cargando…</div>}
        {q.isError && <div className="p-8 text-center text-rose-700 text-base">Error cargando tiendas</div>}
        {q.data && q.data.length === 0 && (
          <div className="p-10 text-center text-slate-400">
            <div className="text-5xl mb-3">🏪</div>
            <p className="text-base">No hay tiendas. Añade la primera.</p>
          </div>
        )}
        <ul className="divide-y divide-slate-100">
          {q.data?.map((s) => (
            <li key={s.id} className="px-5 py-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-lg text-slate-900 truncate">{s.name}</div>
                <div className="text-base text-slate-500 truncate">
                  {s.whatsappNumber ?? 'Sin WhatsApp · los créditos entran por el vendedor'}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => onEdit(s)}
                  aria-label="Editar"
                  className="px-3 py-2 text-base bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full"
                >
                  ✏️
                </button>
                <button
                  type="button"
                  onClick={() => { setError(null); setConfirming(s); }}
                  aria-label="Eliminar"
                  className="px-3 py-2 text-base bg-rose-100 hover:bg-rose-200 text-rose-700 rounded-full"
                >
                  🗑️
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {confirming && (
        <ConfirmDialog
          title="Eliminar tienda"
          message={`¿Seguro que quieres eliminar "${confirming.name}"? Esta acción no se puede deshacer. Si la tienda tiene créditos asociados, no se podrá eliminar.`}
          confirmLabel="Eliminar"
          danger
          pending={delMut.isPending}
          error={error}
          onCancel={() => setConfirming(null)}
          onConfirm={() => delMut.mutate(confirming.id)}
        />
      )}
    </>
  );
}

function StoreDialog({ editing, onClose }: { editing?: Store; onClose: () => void }) {
  const qc = useQueryClient();
  const isEdit = !!editing;
  const [name, setName] = useState(editing?.name ?? '');
  const [phone, setPhone] = useState(editing?.whatsappNumber ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const mut = useMutation({
    mutationFn: () =>
      api<Store>(
        isEdit ? `/api/v1/stores/${editing!.id}` : '/api/v1/stores',
        {
          method: isEdit ? 'PATCH' : 'POST',
          body: { name, whatsappNumber: phone },
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'stores'] });
      qc.invalidateQueries({ queryKey: ['stores'] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Error'),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    mut.mutate();
  };

  return (
    <DialogShell title={isEdit ? 'Editar tienda' : 'Nueva tienda'} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-5">
        <Field label="Nombre" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Ej. Pueblo de Bayamón"
            className={inputCls}
          />
        </Field>
        <Field label="Número de teléfono de contacto" required>
          <input
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            placeholder="(787) 555-1234"
            className={inputCls}
          />
        </Field>
        {error && <ErrorBox text={error} />}
        <DialogActions
          onCancel={onClose}
          submitLabel={isEdit ? 'Guardar cambios' : 'Crear tienda'}
          pending={mut.isPending}
          canSubmit={!!name && !!phone}
        />
      </form>
    </DialogShell>
  );
}

// ----- Users tab -----

function UsersTab({
  currentUserId,
  onAdd,
  onEdit,
}: {
  currentUserId: string;
  onAdd: () => void;
  onEdit: (u: AdminUser) => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api<AdminUser[]>('/api/v1/users'),
  });
  const [confirming, setConfirming] = useState<AdminUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  const delMut = useMutation({
    mutationFn: (id: string) => api(`/api/v1/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      setConfirming(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Error al eliminar'),
  });

  return (
    <>
      <div className="flex justify-end">
        <button
          onClick={onAdd}
          className="px-5 py-3 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white text-base font-bold rounded-full shadow-md"
        >
          + Nuevo usuario
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {q.isLoading && <div className="p-8 text-center text-slate-400 text-base">Cargando…</div>}
        {q.isError && <div className="p-8 text-center text-rose-700 text-base">Error cargando usuarios</div>}
        {q.data && q.data.length === 0 && (
          <div className="p-10 text-center text-slate-400">
            <div className="text-5xl mb-3">👤</div>
            <p className="text-base">No hay usuarios. Añade el primero.</p>
          </div>
        )}
        <ul className="divide-y divide-slate-100">
          {q.data?.map((u) => {
            const isMe = u.id === currentUserId;
            return (
              <li key={u.id} className="px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-lg text-slate-900 truncate">
                      {u.name} {isMe && <span className="text-sm text-slate-400 font-normal">(tú)</span>}
                    </div>
                    <div className="text-base text-slate-500 truncate">
                      {u.email || u.phone || '—'}
                    </div>
                    {u.stores.length > 0 && (
                      <div className="text-sm text-slate-400 mt-0.5 truncate">
                        🏪 {u.stores.map((s) => s.name).join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className="flex flex-col items-end gap-1">
                      {u.roles.map((r) => (
                        <RolePill key={r} role={r} />
                      ))}
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => onEdit(u)}
                        aria-label="Editar"
                        className="px-3 py-1.5 text-base bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => { setError(null); setConfirming(u); }}
                        disabled={isMe}
                        aria-label="Eliminar"
                        className="px-3 py-1.5 text-base bg-rose-100 hover:bg-rose-200 disabled:bg-slate-100 disabled:cursor-not-allowed text-rose-700 rounded-full"
                        title={isMe ? 'No puedes eliminarte a ti mismo' : 'Eliminar usuario'}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {confirming && (
        <ConfirmDialog
          title="Eliminar usuario"
          message={`¿Seguro que quieres eliminar a "${confirming.name}"? Sus créditos quedarán sin asignar pero se preservan en la BD. Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          danger
          pending={delMut.isPending}
          error={error}
          onCancel={() => setConfirming(null)}
          onConfirm={() => delMut.mutate(confirming.id)}
        />
      )}
    </>
  );
}

function RolePill({ role }: { role: AdminUser['roles'][number] }) {
  const map = {
    admin: { label: 'Admin', cls: 'bg-purple-100 text-purple-800' },
    customer_service: { label: 'CS', cls: 'bg-sky-100 text-sky-800' },
    produce_manager: { label: 'Encargado', cls: 'bg-emerald-100 text-emerald-800' },
  } as const;
  const m = map[role];
  return <span className={`text-xs uppercase font-bold px-2.5 py-1 rounded-full ${m.cls}`}>{m.label}</span>;
}

type UserKind = 'produce_manager' | 'customer_service' | 'admin';

function UserDialog({ editing, onClose }: { editing?: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const isEdit = !!editing;
  const initialKind: UserKind = editing
    ? (editing.roles.includes('admin')
        ? 'admin'
        : editing.roles.includes('customer_service')
          ? 'customer_service'
          : 'produce_manager')
    : 'produce_manager';
  const [kind, setKind] = useState<UserKind>(initialKind);
  const [name, setName] = useState(editing?.name ?? '');
  const [phone, setPhone] = useState(editing?.phone ?? '');
  const [storeId, setStoreId] = useState(editing?.stores[0]?.id ?? '');
  const [email, setEmail] = useState(editing?.email ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const storesQ = useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: () => api<Store[]>('/api/v1/stores'),
  });

  const mut = useMutation({
    mutationFn: () => {
      if (isEdit) {
        // Edit: only patch fields that the user could legitimately change.
        const body: Record<string, unknown> = { name };
        if (kind === 'produce_manager') {
          body.phone = phone;
          if (storeId) body.storeId = storeId;
        } else {
          body.email = email;
          if (password) body.password = password;
        }
        return api<{ id: string }>(`/api/v1/users/${editing!.id}`, { method: 'PATCH', body });
      }
      const body =
        kind === 'produce_manager'
          ? { kind, name, phone, storeId }
          : { kind, name, email, password };
      return api<{ id: string }>('/api/v1/users', { method: 'POST', body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Error al crear'),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    mut.mutate();
  };

  const canSubmit =
    !!name &&
    (kind === 'produce_manager'
      ? !!phone && !!storeId
      : !!email && (isEdit || password.length >= 8));

  return (
    <DialogShell title={isEdit ? 'Editar usuario' : 'Nuevo usuario'} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-5">
        <Field label="Tipo">
          <div className="grid grid-cols-3 gap-2">
            {(['produce_manager', 'customer_service', 'admin'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={`px-3 py-3 rounded-xl text-sm font-semibold transition ${kind === k ? 'bg-brand-600 text-white shadow' : 'bg-slate-100 text-slate-700'}`}
              >
                {k === 'produce_manager' ? 'Encargado' : k === 'customer_service' ? 'CS' : 'Admin'}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Nombre" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Ej. Juan Pérez"
            className={inputCls}
          />
        </Field>

        {kind === 'produce_manager' ? (
          <>
            <Field label="Teléfono" required>
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="(787) 555-1234"
                className={inputCls}
              />
            </Field>
            <Field label="Tienda" required>
              <select
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                required
                className={inputCls}
              >
                <option value="">— Selecciona —</option>
                {storesQ.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
          </>
        ) : (
          <>
            <Field label="Email" required>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="usuario@cpe.com"
                className={inputCls}
              />
            </Field>
            <Field label={isEdit ? 'Nueva contraseña (opcional, mínimo 8)' : 'Contraseña inicial (mínimo 8 caracteres)'} required={!isEdit}>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required={!isEdit}
                minLength={isEdit ? undefined : 8}
                placeholder={isEdit ? 'Dejar vacío para no cambiar' : 'Comparte esta contraseña con el usuario'}
                className={`${inputCls} font-mono`}
              />
            </Field>
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
              ⚠️ {isEdit ? 'Si cambias la contraseña, cópiala y compártela antes de cerrar.' : 'La contraseña no se podrá ver de nuevo. Cópiala y compártela con el usuario antes de cerrar.'}
            </p>
          </>
        )}

        {error && <ErrorBox text={error} />}
        <DialogActions
          onCancel={onClose}
          submitLabel={isEdit ? 'Guardar cambios' : 'Crear usuario'}
          pending={mut.isPending}
          canSubmit={canSubmit}
        />
      </form>
    </DialogShell>
  );
}

// ----- Shared bits -----

const inputCls =
  'w-full px-4 py-3.5 text-lg border-2 border-slate-300 rounded-xl focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none';

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-base font-semibold text-slate-700 mb-1.5 block">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      {children}
    </label>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <p className="text-base text-rose-700 bg-rose-50 border-2 border-rose-200 rounded-xl p-3 font-medium">
      {text}
    </p>
  );
}

function DialogShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-xl max-h-[92dvh] overflow-y-auto"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="text-slate-400 hover:text-slate-700 text-3xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function DialogActions({
  onCancel,
  submitLabel,
  pending,
  canSubmit,
}: {
  onCancel: () => void;
  submitLabel: string;
  pending: boolean;
  canSubmit: boolean;
}) {
  return (
    <div className="flex gap-3 pt-1">
      <button
        type="button"
        onClick={onCancel}
        disabled={pending}
        className="flex-1 px-4 py-3.5 border-2 border-slate-300 rounded-full text-slate-700 text-base font-semibold disabled:opacity-50"
      >
        Cancelar
      </button>
      <button
        type="submit"
        disabled={!canSubmit || pending}
        className="flex-1 px-4 py-3.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-base font-bold rounded-full shadow-md"
      >
        {pending ? '…' : submitLabel}
      </button>
    </div>
  );
}

function ConfirmDialog({
  title,
  message,
  confirmLabel,
  danger,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <DialogShell title={title} onClose={onCancel}>
      <p className="text-base text-slate-700 leading-relaxed">{message}</p>
      {error && (
        <div className="mt-4">
          <ErrorBox text={error} />
        </div>
      )}
      <div className="flex gap-3 pt-5">
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="flex-1 px-4 py-3.5 border-2 border-slate-300 rounded-full text-slate-700 text-base font-semibold disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={pending}
          className={`flex-1 px-4 py-3.5 rounded-full text-white text-base font-bold shadow-md disabled:opacity-50 ${
            danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-brand-600 hover:bg-brand-700'
          }`}
        >
          {pending ? '…' : confirmLabel}
        </button>
      </div>
    </DialogShell>
  );
}
